import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import CustomText from "./CustomText";
import { useOktaAuth } from "@okta/okta-react";
import toastMessages from "../../services/utils/helpers/toastMessage";
import { BlockBlobClient } from "@azure/storage-blob";
import useAddDocumentWithPaths from "../../services/utils/custom hooks/Documents/useAddDocumentWithPaths";
import useGetPresignedUrl from "../../services/utils/custom hooks/Documents/useGetPresignedUrl";
import Button from "../organism/Button";
import FileDetail from "../global/FileDetail";
import { useHistory } from "react-router-dom";
import useProfile from "../../services/utils/custom hooks/useProfile";
import ESignatureLoadingIndicator from "../global/LoadingIndicators/ESignatureLoadingIndicator";
import SignatureViewer from "../global/SignatureViewer";
import useSignature from "../../services/utils/custom hooks/eSignature/useSignature";
import FileUploader from "../global/FileUploader";
import MultipleFileUploader from "../global/MultipleFileUploader";
import dataConstants from "../../constants/dataConstants";
import FileIcon from "../../assets/svg/FileIcon";
import { normalizeUploadFile } from "../../services/utils/helpers/fileNameNormalizer";

interface DocumentManagementProps {
  uploadedDocs: any;
  values: any;
  approval?: boolean;
}

const documentLabelByKey: Record<string, string> = {
  patientInfoDemographicSheet: "Patient Info/Demographics sheet",
  insuranceCard: "Insurance Card",
  pathologyReport: "Pathology Report",
  endoscopyReport: "Endoscopy Report",
  currentPrescriptionMedication: "Current Prescription Medications",
  currentPrescriptionMedications: "Current Prescription Medications",
  clinicNotes: "Clinic Notes",
  orderSOF: "Signature on file ( SOF )",
  orderSummary: "Order Summary",
  requisitionForm: "Requisition Form",
  visitNotes: "Visit Note",
  lomn: "LOMN",
  other: "Other",
};

interface ReviewDocumentItem {
  id: string;
  labels: string[];
  filename: string;
  url?: string;
  sizeInBytes?: number;
}

function getFileNameFromPath(path: string): string {
  const safePath = decodeURIComponent(path ?? "").split("?")[0];
  const segments = safePath.split("/").filter(Boolean);
  return segments[segments.length - 1] || "document";
}

function stripTimestampSuffix(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex <= 0) return fileName;
  const base = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex);
  return `${base.replace(/_\d{10,}$/, "")}${extension}`;
}

function getDisplayFileName(path?: string, fallback?: string): string {
  if (path) return stripTimestampSuffix(getFileNameFromPath(path));
  if (fallback) return stripTimestampSuffix(fallback);
  return "document";
}

function getNormalizedPath(path: string): string {
  return decodeURIComponent(path).replace(/^\/+/, "").toLowerCase();
}

function normalizeDocuments(value: any): any[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter(Boolean).map((item) => {
      if (typeof item === "string") {
        return { url: item, filename: getDisplayFileName(item) };
      }
      return item;
    });
  }
  if (typeof value === "string") {
    return [{ url: value, filename: getDisplayFileName(value) }];
  }
  return [value];
}

function AuthorizedESignature({ values }: any) {
  const { data: signatureData, isFetching: fetchingSignatureData } =
    useSignature();

  return (
    <Fragment>
      <p className="font-medium mb-2">Authorized E-Signature</p>
      {fetchingSignatureData ? (
        <ESignatureLoadingIndicator />
      ) : (
        <div className="mb-3">
          <SignatureViewer
            imageURL={signatureData?.viewableURL ?? ""}
            alt="Signature"
            containerClassName="w-[300px]"
            maxHeight="250px"
            maxWidth="300px"
          />
        </div>
      )}
      {values?.eSignatureFiles &&
        values?.eSignatureFiles?.map((e: any) => (
          <FileDetail
            key={e?.name}
            hideRemove
            file={e}
            url={e?.url}
            disabled={false}
            isSummary
          />
        ))}
    </Fragment>
  );
}
const DocumentManagement: React.FC<DocumentManagementProps> = ({
  uploadedDocs,
  values,
  approval,
}) => {
  const [files, setFiles] = useState<any[]>([]);
  const [newFiles, setNewFiles] = useState<any[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const { authState } = useOktaAuth();
  const addDocumentWithPaths = useAddDocumentWithPaths();
  const [transitDocs, setTransitDocs] = useState<any>([]);
  const [localTransitDocuments, setLocalTransitDocuments] = useState<any>([]);
  const [transitDocumentsFiles, setTransitDocumentsFiles] = useState<any>([]);
  const inputRef = useRef<any>();
  const history = useHistory();
  const approvalScreen = history?.location?.pathname?.includes("approval");
  const data = approvalScreen ? values?.supportingSection : values;
  const supportingDocFiles = useMemo(() => {
    const docsData = uploadedDocs?.uploadedDocs || uploadedDocs || {};
    const filesByPath = new Map<string, ReviewDocumentItem>();

    Object.keys(documentLabelByKey).forEach((key) => {
      const normalizedFiles = normalizeDocuments(docsData[key]);
      if (!normalizedFiles.length) return;

      normalizedFiles.forEach((file: any, index: number) => {
        const fileUrl =
          typeof file === "string"
            ? file
            : typeof file?.url === "string"
              ? file.url
              : undefined;
        const fileName =
          typeof file?.filename === "string" && file.filename
            ? getDisplayFileName(fileUrl, file.filename)
            : getDisplayFileName(fileUrl);
        const label = documentLabelByKey[key] ?? key;
        const dedupeKey = fileUrl
          ? getNormalizedPath(fileUrl)
          : `${fileName}-${index}`;
        const existing = filesByPath.get(dedupeKey);
        const mergedLabels = Array.from(
          new Set([...(existing?.labels ?? []), label])
        );

        filesByPath.set(dedupeKey, {
          id: existing?.id ?? `${key}-${index}-${fileUrl ?? fileName}`,
          labels: mergedLabels,
          filename: fileName,
          url: fileUrl ?? existing?.url,
          sizeInBytes:
            typeof file?.size === "number"
              ? file.size
              : existing?.sizeInBytes,
        });
      });
    });

    return Array.from(filesByPath.values()).filter((item) => !!item.filename);
  }, [uploadedDocs]);
  const validateFile = (file: any): boolean => {
    let sizeInMB = null;
    sizeInMB = file.size / 1024 / 1024;

    if (files.length > 0) {
      if (files.some((item: any) => item.name === file.name)) {
        alert("File already added");
        return false;
      }
    }
    function getExt(filename: string): any {
      const ext = filename.split(".").pop();
      if (ext === filename) return "";
      return ext;
    }

    if (!dataConstants.validTypes.includes(getExt(file.name)?.toLowerCase())) {
      toastMessages.error(`Invalid file format`);
      return false;
    }
    if (sizeInMB > 55) {
      alert("File size limit is being exceeded");
      return false;
    }
    return true;
  };
  const showSignature =
    approvalScreen || history.location.pathname.includes("summary");
  const { data: profile } = useProfile();

  function handleFiles(event: any): void {
    let inputFiles = event.target.files;
    let temp: any[] = [];

    if (files.length > 0) {
      temp = [...files];
    }

    let temp2 = [...newFiles];
    for (const element of inputFiles) {
      const normalizedFile = normalizeUploadFile(element);
      if (validateFile(normalizedFile)) {
        temp.push(normalizedFile);
        temp2.push(normalizedFile);
      }
    }
    setFiles(temp);
    setNewFiles(temp2);
  }

  function uploadToAzure(
    inputFile: any,
    url: string,
    filePath: string,
    resolve: any,
    reject: any
  ): void {
    const blockBlobClient = new BlockBlobClient(url);
    blockBlobClient
      .uploadData(inputFile, {
        blobHTTPHeaders: { blobContentType: inputFile.type },
      })
      .then(() => {
        resolve({ filePath, url });
      })
      .catch((error: any) => reject(error));
  }

  const getPresignedUrlHook = useGetPresignedUrl();

  const getPresignedUrl = (inputFile: any, resolve: any, reject: any): any => {
    if (!inputFile) return reject("File missing");
    const normalizedFile = normalizeUploadFile(inputFile);

    getPresignedUrlHook
      .mutateAsync(normalizedFile.name)
      .then((response: any) => {
        const { url, filepath } = response.data;
        if (url && filepath) {
          uploadToAzure(normalizedFile, url, filepath, resolve, reject);
        } else {
          reject("URL or filePath not provided");
        }
      })
      .catch((error: any) => {
        reject(error);
      });
  };

  async function addMoreDocument(orderId: string, transitDocuments: any) {
    setUploading(true);
    try {
      await addDocumentWithPaths.mutateAsync(
        { orderId, transitDocuments },
        {
          onSuccess: () => {
            setNewFiles([]);
            setUploading(false);
          },
          onError: () => {
            setUploading(false);
          },
        }
      );
    } catch (error: any) {
      setUploading(false);
    }
  }

  function uploadFile(inputFile: any) {
    return new Promise((resolve, reject) => {
      getPresignedUrl(
        inputFile,
        (data: any) => {
          uploadToAzure(
            inputFile,
            data.url,
            data.filePath,
            (result: any) => {
              resolve({
                ...result,
                name: inputFile.name,
                size: inputFile.size,
              });
            },
            reject
          );
        },
        reject
      );
    });
  }
  function handleRemove(mainIndex: number) {
    return () => {
      inputRef.current.value = "";
      setNewFiles((prevState: any) =>
        prevState.filter((item: any) => item.name !== files[mainIndex].name)
      );
      setFiles((prevState: any) =>
        prevState.filter((file: any, index: number) => index !== mainIndex)
      );

      if (data?.transitDocuments) {
        let transitDocuments = [...data?.transitDocuments];
        //   let transitDocumentsFiles = [...values.transitDocumentsFiles];

        setTransitDocs(
          transitDocs.filter((item: any, index: number) => index !== mainIndex)
        );
        //   setTransitDocumentsFiles(
        //     transitDocumentsFiles.filter(
        //       (item: any, index: number) => index !== mainIndex
        //     )
        //   );
      }
    };
  }

  function handleTransmit() {
    if (uploading) return;
    setUploading(true);

    Promise.all([...newFiles.map((file) => uploadFile(file))])
      .then((results) => {
        const filteredResults = results.filter((result: any) => result.url);

        const updatedTransitDocs = [...transitDocs, ...filteredResults];
        setTransitDocs(updatedTransitDocs);
        setLocalTransitDocuments(updatedTransitDocs);

        const updatedTransitDocumentsFiles = updatedTransitDocs.map((file) => ({
          ...file,
          existing: true,
        }));
        setTransitDocumentsFiles(updatedTransitDocumentsFiles);

        setFiles((prevFiles) => [
          ...prevFiles.filter((file) => file.url),
          ...filteredResults,
        ]);

        addMoreDocument(
          data?.id,
          filteredResults.map((document: any) => document.filePath)
        );
      })
      .catch((error) => {
        toastMessages.error(error ?? "Something went wrong");
        setUploading(false);
      })
      .finally(() => {
        setUploading(false);
      });
  }

  async function handleDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    let inputFiles = event.dataTransfer.files;
    let temp: any[] = [];

    if (files.length > 0) {
      temp = [...files];
    }

    let temp2 = [...newFiles];
    for (const element of inputFiles) {
      if (validateFile(element)) {
        temp.push(element);
        temp2.push(element);
      }
    }
    setFiles(temp);
    setNewFiles(temp2);
  }

  useEffect(() => {
    if (data?.transitDocument && data?.transitDocument.length > 0) {
      let temp = data.transitDocument.map((file: any, index: number) => {
        let found = data.transitDocument.find(
          (item: any, childIndex: number) => childIndex === index
        );
        return found;
      });
      setFiles(temp);
    }
  }, [data]);

  return (
    <div id="document-management">
      <CustomText
        text="Supporting documents"
        fontWeight="font-medium"
        className="reviewCardHeading my-5"
      />
      <div className="reviewCard">
        <div
          className={`flex ${approval ? "space-between w-full " : "space-x-3"}`}
        >
          {uploadedDocs && supportingDocFiles.length > 0 && (
            <div className="w-full">
              <CustomText
                text="Uploaded Documents"
                fontWeight="font-semibold"
                className="my-2 mt-5"
              />
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                {supportingDocFiles.map((document) => (
                  <div
                    key={document.id}
                    className="border border-roundedInputBorder rounded-lg bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center">
                        <FileIcon className="h-6 w-6 shrink-0" />
                      </div>
                      <div className="min-w-0">
                        {document.url ? (
                          <a
                            href={document.url}
                            target="_blank"
                            rel="noreferrer"
                            title={document.filename}
                            className="block min-w-0"
                          >
                            <CustomText
                              text={document.filename}
                              fontWeight="font-medium"
                              fontSize="text-sm"
                              className="text-royalBlue truncate underline underline-offset-2 hover:opacity-80"
                            />
                          </a>
                        ) : (
                          <CustomText
                            text={document.filename}
                            fontWeight="font-medium"
                            fontSize="text-sm"
                            className="text-royalBlue truncate"
                          />
                        )}
                        {typeof document.sizeInBytes === "number" && (
                          <CustomText
                            text={`${Math.round(document.sizeInBytes / 1024)} kb`}
                            fontWeight="font-normal"
                            fontSize="text-xs"
                            className="text-darkGray"
                          />
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <CustomText
                        text="Uploaded"
                        fontWeight="font-medium"
                        fontSize="text-xs"
                        className="text-darkGray"
                      />
                      <div className="flex flex-wrap items-center justify-end gap-1">
                        {document.labels.map((label) => (
                          <div
                            key={`${document.id}-${label}`}
                            className="inline-flex items-center rounded-full bg-lightBlue px-2 py-1"
                          >
                            <span className="mr-1 inline-block h-2 w-2 rounded-full bg-primary" />
                            <CustomText
                              text={label}
                              fontWeight="font-medium"
                              fontSize="text-xs"
                              className="text-royalBlue"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-between items-center w-full">
          <CustomText
            text="Upload and transmit more documents"
            fontWeight="font-semibold"
          />
          <Button
            buttonTheme="primary"
            textInformation={{
              text: `transmit file (s) ${newFiles?.length > 0 ? `(${newFiles?.length})` : ""
                }`,
              fontWeight: "font-semibold",
            }}
            onClick={handleTransmit}
            disabled={uploading || newFiles?.length === 0}
            loading={uploading}
          />
        </div>
        <MultipleFileUploader
          handleDrop={handleDrop}
          handleRemove={handleRemove}
          files={files}
          inputRef={inputRef}
        />
        <input
          type="file"
          id="file"
          multiple
          accept=".png, .jpeg, .pdf,.docx, .doc/*"
          ref={inputRef}
          onChange={handleFiles}
          style={{ display: "none" }}
        />

        {profile?.user?.roles?.includes("Physician") && showSignature ? (
          <AuthorizedESignature values={values} />
        ) : (
          <Fragment>
            {" "}
            <CustomText text="Signature Allowed" fontWeight="font-semibold" />
            <CustomText
              text={`${data?.signPermission ? "Yes" : "No"}`}
              fontWeight="font-normal"
            />
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default DocumentManagement;
